import type { CSSProperties } from 'react';
import { Link, useParams } from 'react-router';
import { Icon } from '../components/Icon';
import { MoonbasePrice, ProductActions } from '../components/ProductCard';
import { Seo } from '../components/Seo';
import { getProductBySlug } from '../config/site';
import { NotFoundPage } from './NotFoundPage';

export function ProductPage() {
  const { slug = '' } = useParams();
  const product = getProductBySlug(slug);

  if (!product) return <NotFoundPage />;

  return (
    <>
      <Seo
        title={`${product.name} — ${product.eyebrow}`}
        description={product.shortDescription}
        path={`/plugins/${product.slug}`}
        image={product.artwork}
      />
      <section className="product-hero product-hero--emberline section--grain" style={{ '--product-accent': product.accent } as CSSProperties}>
        <div className="shell product-hero__grid">
          <div className="product-hero__copy">
            <Link className="back-link" to="/plugins">← Wavecraft plugins</Link>
            <span className="eyebrow eyebrow--line">{product.category}</span>
            <h1>{product.name}</h1>
            <p className="product-hero__tagline">{product.eyebrow}</p>
            <p className="product-hero__lead">{product.shortDescription}</p>
            <div className="product-hero__price">
              <MoonbasePrice product={product} className="display-price" />
              <span>Launch price · {product.listPriceFallback} after {product.commerce.launchDuration}</span>
            </div>
            <ProductActions product={product} />
            <p className="product-hero__education">
              Education: <strong>{product.commerce.educationPrice}</strong> during launch, then {product.commerce.educationListPrice}. Eligibility required.
            </p>
            <div className="product-hero__trust">
              <span><Icon name="clock" /> {product.commerce.trialDays}-day full trial</span>
              <span><Icon name="key" /> {product.commerce.activations} computers</span>
              <span><Icon name="shield" /> 30-day refund policy</span>
            </div>
          </div>
          <div className="product-hero__visual">
            <div className="product-hero__plate">
              <div className="product-hero__plate-top"><span>{product.eyebrow}</span><span>v{product.version}</span></div>
              <img src={product.artwork} alt={`${product.name} audio processor interface`} />
              <div className="product-hero__plate-bottom">
                <span>{product.formats.join(' · ')}</span>
                <span>Wavecraft / {product.name}</span>
              </div>
            </div>
          </div>
        </div>
      </section>

      <section className="emberline-promise" aria-label="Emberline license and platform highlights">
        <div className="shell emberline-promise__inner">
          <span><strong>Perpetual</strong>Own version 1</span>
          <span><strong>Three activations</strong>Move between your Macs</span>
          <span><strong>Offline capable</strong>Keep working without a connection</span>
          <span><strong>All 1.x updates</strong>Included with purchase</span>
        </div>
      </section>

      <section className="section product-story">
        <div className="shell product-story__grid">
          <div className="product-story__label"><span>01</span><strong>Why it exists</strong></div>
          <div className="product-story__copy">
            <h2>A change you feel before you have to explain it.</h2>
            <p>{product.description}</p>
            <p>Start subtle. Push until the source becomes something new. Pull Mix back when the transformation should live beneath the original rather than replace it.</p>
            <div className="ideal-for">
              <span>Start with</span>
              {product.idealFor.map((use) => <strong key={use}>{use}</strong>)}
            </div>
          </div>
        </div>
      </section>

      <section className="section feature-deep-dive">
        <div className="shell">
          <div className="section-heading">
            <span className="eyebrow">One instrument, three disciplines</span>
            <h2>Explore quickly. Commit carefully.</h2>
          </div>
          <div className="feature-deep-dive__grid">
            {product.features.map((feature, index) => (
              <article key={feature.title}>
                <span>0{index + 1}</span>
                <div className="feature-icon"><Icon name={index === 0 ? 'waveform' : index === 1 ? 'sliders' : 'spark'} /></div>
                <h3>{feature.title}</h3>
                <p>{feature.description}</p>
              </article>
            ))}
          </div>
        </div>
      </section>

      <section className="section product-workflow">
        <div className="shell product-workflow__grid">
          <div><span className="eyebrow">The Emberline workflow</span><h2>Find the feeling, then protect the mix.</h2></div>
          <ol>
            <li><span>1</span><div><strong>Set the input</strong><p>Feed Emberline deliberately—the response follows the energy you give it.</p></div></li>
            <li><span>2</span><div><strong>Turn Amount</strong><p>Move from nearly transparent shaping to a fully transformed character.</p></div></li>
            <li><span>3</span><div><strong>Recover the attack</strong><p>Use ducking when the original transient should lead the processed body.</p></div></li>
            <li><span>4</span><div><strong>Level-match and blend</strong><p>Use output and Mix to judge the final tone without being persuaded by loudness.</p></div></li>
          </ol>
        </div>
      </section>

      <section className="section product-specs">
        <div className="shell product-specs__grid">
          <div>
            <span className="eyebrow">Technical information</span>
            <h2>Ready for the sessions you already run.</h2>
            <p>One macOS installer includes the standalone application, Audio Unit v3 and VST3 formats.</p>
          </div>
          <dl>
            <div><dt>Formats</dt><dd>{product.formats.join(', ')}</dd></div>
            <div><dt>Current version</dt><dd>{product.version}</dd></div>
            <div><dt>Operating system</dt><dd>{product.systemRequirements[0]}</dd></div>
            <div><dt>Processor support</dt><dd>{product.systemRequirements[1]}</dd></div>
            <div><dt>Host requirement</dt><dd>{product.systemRequirements[2]}</dd></div>
            <div><dt>License</dt><dd>Perpetual version 1 · {product.commerce.activations} computers</dd></div>
            <div><dt>Updates</dt><dd>All Emberline 1.x updates included</dd></div>
            <div><dt>Activation</dt><dd>Online or offline through your Moonbase account</dd></div>
          </dl>
        </div>
      </section>

      <section className="section product-final-cta" style={{ '--product-accent': product.accent } as CSSProperties}>
        <div className="shell product-final-cta__inner">
          <div>
            <span className="eyebrow">A beautiful place between sound</span>
            <h2>Meet Emberline.</h2>
            <p>Ten days to explore the complete instrument. No reduced trial mode.</p>
          </div>
          <div><MoonbasePrice product={product} className="display-price" /><ProductActions product={product} /></div>
        </div>
      </section>
    </>
  );
}
